import {
  HiArrowNarrowRight,
  HiOutlineCloud,
  HiOutlineCog,
  HiOutlineDatabase,
  HiOutlineGlobeAlt,
  
} from "react-icons/hi";
import Banner from "../components/Banner.js";
import { HiOutlineWrench,HiOutlineRocketLaunch } from "react-icons/hi2";
import {
  FaDraftingCompass,
  FaFlask,
  FaHandsHelping,
  FaPuzzlePiece,
  FaRocket,
  FaSearch,
} from "react-icons/fa";
import TechStackGrid from "../components/TechStackGrid.js";
import DeliveredProjects from "../components/DeliveredProjects.js";

export default function Service() {
  const problems = [
    "Slow MVP development → missed market timing",
    "Fragile architecture → constant firefighting",
    "Lack of API clarity → frontend-backend misalignment",
    "No CI/CD → risky deployments and downtime",
  ];

  const services = [
    {
      icon: <HiOutlineCog size={28} className="text-yellow-500" />,
      title: "Backend API Development",
      desc: "Secure, scalable, RESTful APIs built for performance.",
    },
    {
      icon: <HiOutlineDatabase size={28} className="text-green-500" />,
      title: "Database Design & Optimization",
      desc: "Robust schema design for relational and NoSQL databases.",
    },
    {
      icon: <HiOutlineGlobeAlt size={28} className="text-purple-500" />,
      title: "Full-Stack Web Applications",
      desc: "From backend logic to frontend UI — we build it all.",
    },
    {
      icon: <HiOutlineCloud size={28} className="text-sky-500" />,
      title: "Cloud Infrastructure & CI/CD",
      desc: "Docker, AWS, GitHub Actions for reliable deployments.",
    },
    {
      icon: <HiOutlineRocketLaunch size={28} className="text-red-500" />,
      title: "Agile Product Development",
      desc: "Sprint-based iterations with clear delivery milestones.",
    },
    {
      icon: <HiOutlineWrench size={28} className="text-blue-500" />,
      title: "End-to-End Product Ownership",
      desc: "From discovery to deployment, we handle everything.",
    },
  ];

  const steps = [
    {
      icon: FaSearch,
      title: "Discovery & Planning",
      desc: "Understand goals & define scope.",
      color: "text-yellow-500",
    },
    {
      icon: FaDraftingCompass,
      title: "Architecture & Design",
      desc: "Create scalable system design.",
      color: "text-green-500",
    },
    {
      icon: FaPuzzlePiece,
      title: "Agile Development",
      desc: "Sprint-based feature delivery.",
      color: "text-pink-500",
    },
    {
      icon: FaFlask,
      title: "QA & Testing",
      desc: "Manual & automated testing flows.",
      color: "text-purple-500",
    },
    {
      icon: FaRocket,
      title: "Deployment",
      desc: "CI/CD with rollback support.",
      color: "text-red-500",
    },
    {
      icon: FaHandsHelping,
      title: "Post-launch Support",
      desc: "We stay with you post-launch.",
      color: "text-blue-500",
    },
  ];

  return (
    <>
      <Banner />

      <div className="bg-white text-gray-800 container">
        {/* 2. Problem We Solve */}
        <section className="py-16 px-6 bg-white text-center">
          <h2 className="">
            Backend Problems Are Business Problems
          </h2>
          <p className="text-gray-600 mb-10">
            Startups struggle to scale. Enterprises battle legacy systems.
            Internal tools don’t keep up. Sound familiar?
            <br />
            We help you build backend-first systems that scale, integrate, and
            just work.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:shadow-sm transition"
              >
                <HiArrowNarrowRight className="text-blue-600 mt-1" size={20} />
                <p className="text-gray-700">{problem}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 3. Services Section */}
        <section className="bg-gray-50 py-10 px-6">
          <div>
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-10">
              Our Web & Backend Development Services
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {services.map(({ icon, title, desc }, i) => (
                <div
                  key={i}
                  className="bg-white p-6 rounded-2xl shadow hover:shadow-md transition"
                >
                  <div className="flex items-center gap-3">
                    
                    <p>{icon}</p>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Tech Stack */}
        <section className="bg-gray-50 py-15 px-6">
          <TechStackGrid />
        </section>

        {/* 5. How We Work */}
        <section className="text-center py-16 px-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-10">
            From Discovery to Deployment — We’ve Got You Covered
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map(({ icon: Icon, title, desc, color }, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow">
                <Icon className={`text-2xl mb-3 ${color}`} />
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  {title}
                </h3>
                <p className="text-gray-700">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 6. Why Choose Us */}
        <section className="py-10 px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-10">
            What Makes Enginuity Different?
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto text-gray-700">
            {[
              "✅ Backend-first mindset",
              "✅ CI/CD + Docker + Cloud-native setup",
              "✅ Transparent, Agile delivery",
              "✅ 99.9% uptime-ready systems",
              "✅ Built by real engineers — not freelancers",
              "✅ Post-launch support included",
            ].map((point, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 shadow">
                {point}
              </div>
            ))}
          </div>
          {/* Optional Testimonial */}
          {/* <div className="mt-10 max-w-2xl mx-auto text-gray-600 italic">
            "Enginuity delivered a backend that scales with our user base.
            Reliable, responsive, and highly professional."
            <div className="mt-2 font-semibold text-gray-800">
              — CTO, @Dev.Net SS
            </div>
          </div> */}
        </section>

        {/* 7. Who We Help */}

        <DeliveredProjects />
        
        {/* 8. Final CTA */}
      
        {/* <RequestProposal /> */}
      </div>
    </>
  );
}
